// Insights-tab card: surfaces correlations between logged triggers and
// outcomes (Menopause Score / symptom severity / hot flashes). Three render
// states: A (no data CTA), B (some logging, no qualifying insights yet),
// C (one or more insight cards). Pure presentation — analysis lives in
// triggerCorrelations.ts.
import { useEffect, useState } from "react";
import {
  computeTriggerCorrelations,
  countTriggerDays,
  type CorrelationInsight,
  type ConfidenceLevel,
  type TriggerKey,
  type OutcomeKey,
} from "./triggerCorrelations";

// Brand tokens — kept in sync with KnowMenoApp.tsx.
const T = "#FFC1CD";
const TL = "#FFE4EA";
const TD = "#4B1F4F";
const GD = "#6E4B71";
const GR = "#6E4B71";
const GL = "#FAF7F5";
const WH = "#ffffff";
const RO = "#FF5C7A";
const RL = "#FFE4EA";
const TEAL = "#1D9E75";
const TEAL_L = "#D8F1E6";
const AMBER = "#D9A441";
const AMBER_L = "#FBEFD3";

const TRIGGER_META: Record<TriggerKey, { emoji: string; label: string }> = {
  caffeine: { emoji: "☕", label: "Caffeine" },
  alcohol: { emoji: "🍷", label: "Alcohol" },
  exercise: { emoji: "🏃", label: "Exercise" },
  sleep: { emoji: "😴", label: "Sleep" },
  stress: { emoji: "😰", label: "Stress" },
};

const OUTCOME_LABEL: Record<OutcomeKey, string> = {
  meno_score: "Menopause Score",
  symptom_severity: "Symptom Severity",
  hot_flash_frequency: "Hot Flashes",
};

function ConfidenceDot({ level, days }: { level: ConfidenceLevel; days: number }) {
  const map = {
    low: { bg: AMBER_L, fg: AMBER, text: `Low confidence — ${days} day${days === 1 ? "" : "s"} of data` },
    building: { bg: TL, fg: RO, text: `Building confidence — ${days} day${days === 1 ? "" : "s"} of data` },
    confident: { bg: TEAL_L, fg: TEAL, text: `Confident — ${days} days of data` },
  } as const;
  const s = map[level];
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, color: GR }}>
      <span style={{ width: 8, height: 8, borderRadius: "50%", background: s.fg, flexShrink: 0 }} />
      <span style={{ color: s.fg, fontWeight: 500 }}>{s.text}</span>
    </div>
  );
}

function EffectBadge({ insight }: { insight: CorrelationInsight }) {
  if (insight.direction === "unclear") {
    return (
      <span style={{ fontSize: 11, fontWeight: 500, color: GR, background: GL, borderRadius: 10, padding: "4px 10px" }}>
        Effect not clear yet
      </span>
    );
  }
  const { outcome, effectSize } = insight;
  const abs = Math.abs(effectSize);
  const arrow = effectSize > 0 ? "↑" : "↓";
  const unit = outcome === "meno_score" ? "score" : outcome === "hot_flash_frequency" ? "hot flashes" : "severity";
  const num = outcome === "meno_score" ? Math.round(abs).toString() : abs.toFixed(1);
  const helpful = insight.direction === "helpful";
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        color: helpful ? RO : TD,
        background: helpful ? TL : "#EEDEF0",
        borderRadius: 10,
        padding: "4px 10px",
        whiteSpace: "nowrap",
      }}
    >
      {arrow} {num} {unit}
    </span>
  );
}

function InsightCard({ insight }: { insight: CorrelationInsight }) {
  const meta = TRIGGER_META[insight.trigger];
  const action =
    insight.direction === "harmful" ? "Consider tracking how this changes when you reduce intake."
    : insight.direction === "helpful" ? "Keep doing what's working."
    : insight.direction === "unclear" ? "More data will clarify this."
    : "Watch how this shifts as you log more.";
  return (
    <div
      style={{
        background: WH,
        border: "0.5px solid #E8D5DA",
        borderRadius: 18,
        padding: "16px 18px",
        animation: "tic-fade 320ms ease both",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 6 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <span style={{ fontSize: 18 }}>{meta.emoji}</span>
          <span style={{ fontSize: 14, fontWeight: 500, color: TD }}>{meta.label}</span>
          <span style={{ fontSize: 11, color: GR }}>· {OUTCOME_LABEL[insight.outcome]}</span>
        </div>
        <EffectBadge insight={insight} />
      </div>
      <div style={{ fontSize: 13, color: GD, lineHeight: 1.55, marginBottom: 10 }}>
        {insight.effectDescription}
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
        <ConfidenceDot level={insight.confidence} days={insight.daysAnalyzed} />
        <div style={{ fontSize: 11, color: GR, fontStyle: "italic" }}>{action}</div>
      </div>
    </div>
  );
}

export function TriggerInsightsCard({ userId }: { userId: string }) {
  const [loading, setLoading] = useState(true);
  const [insights, setInsights] = useState<CorrelationInsight[]>([]);
  const [daysLogged, setDaysLogged] = useState(0);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [ins, days] = await Promise.all([
          computeTriggerCorrelations(userId, 30),
          countTriggerDays(userId, 30),
        ]);
        if (!cancelled) {
          setInsights(ins);
          setDaysLogged(days);
        }
      } catch (e) {
        console.warn("[TriggerInsights] failed", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  if (loading) {
    return (
      <div style={{ background: GL, borderRadius: 18, padding: "18px 20px", marginBottom: 14, fontSize: 13, color: GR }}>
        Crunching your trigger patterns…
      </div>
    );
  }

  // STATE A — no data yet
  if (daysLogged === 0 && insights.length === 0) {
    return (
      <div style={{ background: WH, border: "0.5px solid #E8D5DA", borderRadius: 18, padding: "18px 20px", marginBottom: 14 }}>
        <div style={{ fontSize: 10, color: RO, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 500, marginBottom: 8 }}>Triggers</div>
        <div style={{ fontSize: 16, fontWeight: 500, color: GD, marginBottom: 6 }}>Trigger Insights</div>
        <div style={{ fontSize: 13, color: GR, lineHeight: 1.55, marginBottom: 10 }}>
          Start logging triggers to see how they may affect your symptoms and score.
        </div>
        <div style={{ fontSize: 12, color: TD, fontWeight: 500 }}>Log triggers on the Today tab →</div>
      </div>
    );
  }

  // STATE B — some logging, no qualifying insights
  if (insights.length === 0) {
    return (
      <div style={{ background: WH, border: "0.5px solid #E8D5DA", borderRadius: 18, padding: "18px 20px", marginBottom: 14 }}>
        <div style={{ fontSize: 10, color: RO, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 500, marginBottom: 8 }}>Triggers</div>
        <div style={{ fontSize: 16, fontWeight: 500, color: GD, marginBottom: 6 }}>Trigger Insights</div>
        <div style={{ fontSize: 13, color: GR, lineHeight: 1.55, marginBottom: 10 }}>
          Keep logging — patterns will emerge as you accumulate more data.
        </div>
        <div style={{ fontSize: 12, color: TD, fontWeight: 500 }}>{daysLogged} day{daysLogged === 1 ? "" : "s"} of trigger data so far</div>
      </div>
    );
  }

  // STATE C — show top insights
  const top = insights.slice(0, 5);
  return (
    <div style={{ marginBottom: 14 }}>
      <style>{`@keyframes tic-fade { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }`}</style>
      <div style={{ background: WH, border: "0.5px solid #E8D5DA", borderRadius: 18, padding: "16px 18px", marginBottom: 10 }}>
        <div style={{ fontSize: 10, color: RO, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 500, marginBottom: 6 }}>Triggers</div>
        <div style={{ fontSize: 16, fontWeight: 500, color: GD, marginBottom: 10 }}>Trigger Insights — Last 30 Days</div>
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 11, color: GR }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: AMBER }} /> Low
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: RO }} /> Building
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: TEAL }} /> Confident
          </span>
        </div>
      </div>
      <div style={{ display: "grid", gap: 10, gridTemplateColumns: "1fr" }}>
        {top.map((ins, i) => (
          <InsightCard key={`${ins.trigger}-${ins.outcome}-${i}`} insight={ins} />
        ))}
      </div>
    </div>
  );
}
